import type { ReactNode } from 'react'

import { UserType } from '@/constants/roles'

export interface NavItem {
  label: string
  to: string
  icon: ReactNode
  roles: readonly UserType[]
}

export interface NavCategory {
  category: string
  items: NavItem[]
}

// Phosphor-style outline icons, matching the Zynix theme's own SVG icon
// convention (viewBox 0 0 256 256, stroke="currentColor", stroke-width 16).
const DashboardIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 256 256">
    <rect width="256" height="256" fill="none" />
    <path
      d="M104,216V152h48v64h64V120a8,8,0,0,0-2.34-5.66l-80-80a8,8,0,0,0-11.32,0l-80,80A8,8,0,0,0,40,120v96Z"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
  </svg>
)

const ProfileIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 256 256">
    <rect width="256" height="256" fill="none" />
    <circle
      cx="128"
      cy="120"
      r="40"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <path
      d="M63.8,199.37a72,72,0,0,1,128.4,0"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <circle
      cx="128"
      cy="128"
      r="96"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
  </svg>
)

const JobsIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 256 256">
    <rect width="256" height="256" fill="none" />
    <rect
      x="40"
      y="88"
      width="176"
      height="128"
      rx="8"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <path
      d="M88,88V64a8,8,0,0,1,8-8h64a8,8,0,0,1,8,8V88"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
  </svg>
)

const ApplicationsIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 256 256">
    <rect width="256" height="256" fill="none" />
    <path
      d="M200,224H56a8,8,0,0,1-8-8V40a8,8,0,0,1,8-8h96l56,56V216A8,8,0,0,1,200,224Z"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <line
      x1="88"
      y1="136"
      x2="168"
      y2="136"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <line
      x1="88"
      y1="168"
      x2="168"
      y2="168"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
  </svg>
)

const OrganizationIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 256 256">
    <rect width="256" height="256" fill="none" />
    <rect
      x="48"
      y="40"
      width="160"
      height="176"
      rx="8"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <line
      x1="88"
      y1="80"
      x2="88"
      y2="80.01"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="24"
    />
    <line
      x1="128"
      y1="80"
      x2="128"
      y2="80.01"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="24"
    />
    <line
      x1="168"
      y1="80"
      x2="168"
      y2="80.01"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="24"
    />
    <line
      x1="88"
      y1="120"
      x2="88"
      y2="120.01"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="24"
    />
    <line
      x1="128"
      y1="120"
      x2="128"
      y2="120.01"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="24"
    />
    <line
      x1="168"
      y1="120"
      x2="168"
      y2="120.01"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="24"
    />
  </svg>
)

const TeamIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 256 256">
    <rect width="256" height="256" fill="none" />
    <circle
      cx="88"
      cy="108"
      r="52"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <path
      d="M155.41,60A52,52,0,1,1,169.6,161.9"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <path
      d="M17.53,199.68a88,88,0,0,1,140.94,0"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <path
      d="M187.36,144a88,88,0,0,1,51.11,55.68"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
  </svg>
)

const CandidatesIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 256 256">
    <rect width="256" height="256" fill="none" />
    <circle
      cx="128"
      cy="96"
      r="64"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <path
      d="M32,224a97.9,97.9,0,0,1,192,0"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <line
      x1="200"
      y1="88"
      x2="240"
      y2="88"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
  </svg>
)

const TasksIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 256 256">
    <rect width="256" height="256" fill="none" />
    <path
      d="M216,56V208a8,8,0,0,1-8,8H48a8,8,0,0,1-8-8V56a8,8,0,0,1,8-8H208A8,8,0,0,1,216,56Z"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <polyline
      points="88 136 112 160 168 104"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
  </svg>
)

const HiredIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 256 256">
    <rect width="256" height="256" fill="none" />
    <circle
      cx="108"
      cy="100"
      r="60"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <path
      d="M22,200a108,108,0,0,1,146-28"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <polyline
      points="160 196 188 224 244 168"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
  </svg>
)

const IntegrationsIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 256 256">
    <rect width="256" height="256" fill="none" />
    <circle
      cx="64"
      cy="128"
      r="32"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <circle
      cx="192"
      cy="72"
      r="32"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <circle
      cx="192"
      cy="184"
      r="32"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <line
      x1="91"
      y1="112"
      x2="165"
      y2="88"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <line
      x1="91"
      y1="144"
      x2="165"
      y2="168"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
  </svg>
)

const AdminDashIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 256 256">
    <rect width="256" height="256" fill="none" />
    <rect x="16" y="16" width="92" height="92" rx="8" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="16" />
    <rect x="148" y="16" width="92" height="92" rx="8" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="16" />
    <rect x="16" y="148" width="92" height="92" rx="8" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="16" />
    <rect x="148" y="148" width="92" height="92" rx="8" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="16" />
  </svg>
)

const AllUsersIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 256 256">
    <rect width="256" height="256" fill="none" />
    <circle
      cx="88"
      cy="108"
      r="52"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <path
      d="M155.41,60A52,52,0,1,1,169.6,161.9"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <path
      d="M17.53,199.68a88,88,0,0,1,140.94,0"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <path
      d="M187.36,144a88,88,0,0,1,51.11,55.68"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
  </svg>
)

const AllOrgsIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 256 256">
    <rect width="256" height="256" fill="none" />
    <rect
      x="16"
      y="96"
      width="88"
      height="128"
      rx="8"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <path
      d="M104,48H208a8,8,0,0,1,8,8V224H104"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <line
      x1="144"
      y1="88"
      x2="144"
      y2="88.01"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="24"
    />
    <line
      x1="176"
      y1="88"
      x2="176"
      y2="88.01"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="24"
    />
    <line
      x1="144"
      y1="128"
      x2="144"
      y2="128.01"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="24"
    />
    <line
      x1="176"
      y1="128"
      x2="176"
      y2="128.01"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="24"
    />
    <line
      x1="48"
      y1="136"
      x2="48"
      y2="136.01"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="24"
    />
    <rect
      x="128"
      y="192"
      width="32"
      height="32"
      rx="2"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
  </svg>
)

const NON_ADMIN_ROLES = [UserType.Student, UserType.RecruiterUser, UserType.RecruiterAdmin]

/**
 * Only lists routes that exist (or are about to). Recruiter/Admin portals
 * get their own entries here once those phases ship real pages — see
 * docs/architecture/decisions.md, "Phase 4: Theme integration".
 */
const NAV_CATEGORIES: NavCategory[] = [
  {
    category: 'Main',
    items: [
      {
        label: 'Dashboard',
        to: '/',
        icon: DashboardIcon,
        roles: NON_ADMIN_ROLES,
      },
      {
        label: 'Dashboard',
        to: '/admin/dashboard',
        icon: AdminDashIcon,
        roles: [UserType.SuperAdmin],
      },
      // Notifications sidebar link removed — the header bell (NotificationBell)
      // is the primary entry point now; /notifications itself still exists
      // for its "View all" link.
    ],
  },
  {
    category: 'Profile',
    items: [
      {
        label: 'My Profile',
        to: '/profile',
        icon: ProfileIcon,
        roles: [UserType.Student],
      },
    ],
  },
  {
    category: 'Jobs',
    items: [
      {
        label: 'Browse Jobs',
        to: '/jobs',
        icon: JobsIcon,
        roles: [UserType.Student],
      },
      {
        label: 'My Applications',
        to: '/applications',
        icon: ApplicationsIcon,
        roles: [UserType.Student],
      },
    ],
  },
  {
    category: 'Recruiting',
    items: [
      {
        label: 'Job Postings',
        to: '/recruiter/jobs',
        icon: JobsIcon,
        roles: [UserType.RecruiterUser, UserType.RecruiterAdmin],
      },
      {
        label: 'Candidates',
        to: '/recruiter/candidates-v2',
        icon: CandidatesIcon,
        roles: [UserType.RecruiterUser, UserType.RecruiterAdmin],
      },
      {
        label: 'All Candidates',
        to: '/recruiter/all-candidates',
        icon: CandidatesIcon,
        roles: [UserType.RecruiterUser, UserType.RecruiterAdmin],
      },
      {
        label: 'Hired Candidates',
        to: '/recruiter/hires',
        icon: HiredIcon,
        roles: [UserType.RecruiterUser, UserType.RecruiterAdmin],
      },
      {
        label: 'My Tasks',
        to: '/recruiter/tasks',
        icon: TasksIcon,
        roles: [UserType.RecruiterUser, UserType.RecruiterAdmin],
      },
    ],
  },
  {
    category: 'Organization',
    items: [
      {
        label: 'Organization Profile',
        to: '/recruiter/organization',
        icon: OrganizationIcon,
        roles: [UserType.RecruiterUser, UserType.RecruiterAdmin],
      },
      {
        label: 'Team',
        to: '/recruiter/team',
        icon: TeamIcon,
        roles: [UserType.RecruiterAdmin],
      },
    ],
  },
  {
    category: 'Administration',
    items: [
      {
        label: 'All Users',
        to: '/admin/users',
        icon: AllUsersIcon,
        roles: [UserType.SuperAdmin],
      },
      {
        label: 'Organizations',
        to: '/admin/organizations',
        icon: AllOrgsIcon,
        roles: [UserType.SuperAdmin],
      },
      {
        label: 'Hired Candidates',
        to: '/admin/hires',
        icon: HiredIcon,
        roles: [UserType.SuperAdmin],
      },
      {
        label: 'LMS Integrations',
        to: '/admin/lms-sources',
        icon: IntegrationsIcon,
        roles: [UserType.SuperAdmin],
      },
    ],
  },
]

export function getNavCategoriesForRole(role: UserType): NavCategory[] {
  return NAV_CATEGORIES.map((category) => ({
    category: category.category,
    items: category.items.filter((item) => item.roles.includes(role)),
  })).filter((category) => category.items.length > 0)
}
